import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronRight, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Header } from "@/components/site/Header";
import { ReadingProgressBar } from "@/components/site/ReadingProgressBar";
import { Footer } from "@/components/site/Footer";

const Container = ({ children, className = "" }) => (
  <div className={`relative mx-auto w-full max-w-[1288px] px-4 sm:px-6 lg:px-8 ${className}`}>
    {children}
  </div>
);

/* ---------------- Table of contents ---------------- */

const TOC = [
  { id: "what-is-a-stablecoin", title: "What Is a Stablecoin" },
  { id: "why-were-stablecoins-created", title: "Why Were Stablecoins Created" },
  { id: "how-stablecoins-work", title: "How Stablecoins Work" },
  { id: "types-of-stablecoins", title: "Types of Stablecoins" },
  { id: "stablecoins-among-other-forms-of-money", title: "Stablecoins Among Other Forms of Money" },
  { id: "where-businesses-use-stablecoins-today", title: "Where Businesses Use Stablecoins Today" },
  { id: "how-a-transaction-becomes-a-financial-record", title: "How a Transaction Becomes a Financial Record" },
  { id: "how-stablecoins-are-accounted-for-and-taxed", title: "How Stablecoins Are Accounted for and Taxed Around the World" },
  { id: "benefits-and-considerations", title: "Benefits and Considerations of Stablecoins" },
  { id: "future-of-stablecoins", title: "The Future of Stablecoins in Business Finance" },
];

/* ---------------- Shared typographic atoms ---------------- */

const P = ({ children, className = "" }) => (
  <p className={`mt-5 text-base leading-[1.75] text-muted-foreground ${className}`}>{children}</p>
);

const H3 = ({ children }) => (
  <h3 className="mt-10 text-xl font-semibold tracking-[-0.01em] text-foreground">{children}</h3>
);

const Note = ({ children, testId }) => (
  <div
    className="mt-6 rounded-xl border-l-[3px] border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/[0.05] px-6 py-5"
    data-testid={testId}
  >
    <p className="text-[0.95rem] leading-relaxed text-foreground/80">{children}</p>
  </div>
);

const Section = ({ id, index, title, children }) => (
  <section id={id} className="scroll-mt-28 pt-14 first:pt-0" data-testid={`article-section-${id}`}>
    <div className="flex items-baseline gap-4">
      <span className="text-sm font-medium tracking-[0.08em] text-[hsl(174_86%_24%)]">
        {String(index).padStart(2, "0")}
      </span>
      <h2 className="text-2xl font-semibold tracking-[-0.02em] text-foreground sm:text-[1.7rem]">
        {title}
      </h2>
    </div>
    <div className="mt-1 border-t border-[hsl(var(--primary))]/10 pt-1">{children}</div>
  </section>
);

/* ---------------- Use cases data (Section 6) ---------------- */

const USE_CASES = [
  [
    "International Payments",
    "Companies operating across multiple countries often manage payments across different currencies, banking systems, and regulatory environments. Stablecoins can provide an additional way to settle payments with international partners and suppliers through digital networks. For certain use cases, this can simplify cross-border payment workflows and reduce reliance on multiple intermediaries.",
  ],
  [
    "Contractor and Freelancer Payments",
    "The growth of distributed teams has changed how companies manage global payouts. Businesses increasingly work with contractors and service providers across different countries and need reliable ways to pay a large number of recipients. Stablecoins can be one option for contractor payments, especially when recipients need access to digital payment networks or when traditional payment channels create delays.",
  ],
  [
    "Marketplace and Platform Payouts",
    "Marketplaces and digital platforms often need to send payments to many participants, including sellers, creators, and service providers. Managing a high volume of smaller payments requires scalable workflows, clear records, and consistent payment processes. Stablecoins can support these models by providing a digital settlement option across different regions.",
  ],
  [
    "Treasury Operations",
    "Some companies also explore stablecoins for treasury activities, including liquidity management and internal settlement processes. These use cases require a stronger understanding of operational controls, risk management, and regulatory requirements. As a result, many businesses evaluate treasury applications only after establishing clear processes for payments and reporting.",
  ],
];

/* ---------------- Blocks ---------------- */

const ArticleHero = () => (
  <section data-testid="article-hero-section">
    <Container className="pt-[41px] sm:pt-[49px] lg:pt-[90px]">
      {/* Breadcrumb */}
      <nav className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground" aria-label="Breadcrumb" data-testid="article-breadcrumb">
        <Link
          to="/resources"
          className="transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm"
          data-testid="article-breadcrumb-resources-link"
        >
          Resources
        </Link>
        <ChevronRight className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden="true" />
        <span className="text-foreground/80">Understanding Stablecoins in Business Finance</span>
      </nav>

      {/* Hero panel — title, meta, and tags on a soft tinted canvas (per reference) */}
      <div
        className="mt-10 rounded-3xl bg-[hsl(var(--primary))]/[0.05] px-[14px] py-[33px] sm:px-[38px] sm:py-[49px] lg:px-[54px] lg:py-[65px]"
        data-testid="article-hero-panel"
      >
        <h1 className="font-serif-editorial max-w-4xl text-[2.4rem] font-semibold leading-[1.08] tracking-[-0.03em] text-foreground [text-wrap:balance] sm:text-5xl lg:text-[3.6rem]">
          Understanding Stablecoins in Business Finance
        </h1>
        <div className="mt-7 flex flex-wrap items-center gap-x-5 gap-y-3">
          <span className="flex items-center gap-2.5">
            <span className="h-[3px] w-5 bg-[hsl(var(--accent))]" aria-hidden="true" />
            <span className="text-xs font-semibold uppercase tracking-[0.18em] text-[hsl(174_86%_24%)]">
              Guide
            </span>
          </span>
          <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Clock className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden="true" />
            13 min read
          </span>
        </div>
        <div className="mt-8 max-w-4xl lg:max-w-[1080px]">
          <p className="text-base leading-relaxed text-muted-foreground sm:text-lg">
            Stablecoins are finding a place in business finance, particularly
            in cross-border payments and global payout workflows. For finance
            teams, understanding how they work is the first step toward
            evaluating where they fit within existing financial operations.
          </p>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground sm:text-lg">
            Stablecoins have grown significantly over the past few years. As of
            2025, the combined market capitalization of the largest stablecoins
            exceeded USD 220 billion, and businesses are increasingly
            evaluating where they fit into payment and treasury workflows.
          </p>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground sm:text-lg">
            This guide covers what stablecoins are, why they were created, how
            they compare with traditional money and other digital assets, where
            businesses use them today, and what finance teams should consider
            before using them.
          </p>
        </div>
        <div className="mt-8 flex flex-wrap gap-2.5" data-testid="article-hero-tags">
          {["Stablecoins", "Payments"].map((t) => (
            <span
              key={t}
              className="rounded-full border border-[hsl(var(--primary))]/25 px-4 py-1.5 text-sm font-medium text-foreground/80"
            >
              {t}
            </span>
          ))}
        </div>
      </div>
    </Container>
  </section>
);

/* Sticky table of contents with scrollspy */
const TocSidebar = () => {
  const [activeId, setActiveId] = useState(TOC[0].id);

  useEffect(() => {
    const onScroll = () => {
      let current = TOC[0].id;
      for (const item of TOC) {
        const el = document.getElementById(item.id);
        if (el && el.getBoundingClientRect().top <= 140) current = item.id;
      }
      setActiveId(current);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleClick = (e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 100;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  return (
    <aside className="hidden lg:block">
      <nav
        className="sticky top-24 rounded-2xl bg-card/80 p-7 px-[23px] shadow-[0_16px_40px_-24px_hsl(218_51%_15%/0.3)] ring-1 ring-[hsl(var(--primary))]/10 backdrop-blur-[2px]"
        aria-label="Table of contents"
        data-testid="article-toc"
      >
        <h2 className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
          Table of Contents
        </h2>
        <ol className="mt-5 space-y-1">
          {TOC.map((s) => (
            <li key={s.id}>
              <a
                href={`#${s.id}`}
                onClick={(e) => handleClick(e, s.id)}
                className={`block rounded-md py-1.5 pr-2 text-sm leading-snug transition-colors hover:text-[hsl(174_86%_24%)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                  activeId === s.id
                    ? "font-medium text-[hsl(174_86%_24%)]"
                    : "text-muted-foreground"
                }`}
                data-testid={`article-toc-link-${s.id}`}
              >
                {s.title}
              </a>
            </li>
          ))}
        </ol>
      </nav>
    </aside>
  );
};

const ArticleBody = () => (
  <section data-testid="article-body-section">
    <Container>
      <div className="grid gap-12 lg:grid-cols-[300px_minmax(0,1fr)] lg:gap-16">
        <TocSidebar />

        <article className="max-w-[76ch]" data-testid="article-content">
          {/* 01 — What Is a Stablecoin */}
          <Section id="what-is-a-stablecoin" index={1} title="What Is a Stablecoin">
            <P>
              A stablecoin is a digital asset designed to maintain a stable
              value. Unlike cryptocurrencies such as Bitcoin or Ethereum, which
              can experience significant price fluctuations, stablecoins are
              usually tied to a more stable asset — most commonly a traditional
              currency. For example, one USDC is designed to maintain a value
              close to one U.S. dollar (1 USDC ≈ 1 USD).
            </P>
          </Section>

          {/* 02 — Why Were Stablecoins Created */}
          <Section id="why-were-stablecoins-created" index={2} title="Why Were Stablecoins Created">
            <P>
              The first cryptocurrencies showed that value could be transferred
              directly between users without relying on traditional financial
              intermediaries. But they also introduced a major challenge: price
              volatility.
            </P>
            <P>
              For example, imagine a company receives a $50,000 payment in
              Bitcoin. If the price drops by 10% a few days later, the value of
              that payment falls to approximately $45,000.
            </P>
            <P>
              For investors, this volatility can be part of a broader
              investment strategy. For businesses, it creates practical
              challenges. Planning expenses, managing cash flow, and paying
              suppliers become more difficult when the value of an asset can
              change significantly in a short period of time.
            </P>
            <Note testId="article-note-why-created">
              Stablecoins were created to address this challenge. They aim to
              combine the benefits of digital payments with the price stability
              businesses need for everyday financial operations.
            </Note>
          </Section>

          {/* 03 — How Stablecoins Work */}
          <Section id="how-stablecoins-work" index={3} title="How Stablecoins Work">
            <P>
              To understand how stablecoins can fit into business operations,
              it helps to look at what happens behind the scenes: how tokens
              are issued, how they maintain their value, and how transactions
              are processed. The process relies on three key elements:
              issuance, value stability, and blockchain-based transfers.
            </P>
            <div className="mt-8 space-y-5" data-testid="article-how-it-works-cards">
              {[
                {
                  t: "Issuance",
                  d: "A stablecoin is created by an issuer or protocol that generates digital tokens representing a specific value. These tokens can then be transferred between users, businesses, or platforms.",
                },
                {
                  t: "Maintaining Price Stability",
                  d: "Most stablecoins maintain their value through reserves. The issuer holds assets such as cash or liquid securities that support the number of tokens in circulation.",
                },
                {
                  t: "Transfers Through Blockchain Networks",
                  d: "Stablecoin transactions are recorded on a blockchain — a distributed digital ledger that tracks each transfer. The record shows key transaction details, including the sender, recipient, amount, timestamp, and network used.",
                },
              ].map((c) => (
                <div
                  key={c.t}
                  className="rounded-2xl bg-card/80 p-6 shadow-[0_16px_40px_-24px_hsl(218_51%_15%/0.3)] ring-1 ring-[hsl(var(--primary))]/10 backdrop-blur-[2px]"
                >
                  <span className="block h-[3px] w-10 bg-[hsl(var(--accent))]" aria-hidden="true" />
                  <h3 className="mt-4 text-base font-semibold tracking-[-0.01em] text-foreground">
                    {c.t}
                  </h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-muted-foreground">{c.d}</p>
                </div>
              ))}
            </div>
            <Note testId="article-note-blockchain-record">
              For businesses, there is an important distinction: a blockchain
              transaction is not the same as a complete financial record. While
              the blockchain captures the movement of assets, finance teams
              also need business context and supporting documentation for
              accounting, reporting, and compliance.
            </Note>
          </Section>

          {/* 04 — Types of Stablecoins */}
          <Section id="types-of-stablecoins" index={4} title="Types of Stablecoins">
            <P>
              Not all stablecoins work the same way. They use different
              mechanisms to maintain their value, and this difference matters
              for businesses evaluating stablecoins as part of their payment
              infrastructure. The type of stablecoin affects its risk profile,
              price stability, and the level of oversight a finance team may
              need when using it.
            </P>
            <H3>Fiat-Backed Stablecoins</H3>
            <P>
              Fiat-backed stablecoins are the most common type. Their value is
              linked to traditional currencies, most often the U.S. dollar or
              euro, and supported by reserves such as cash or highly liquid
              assets. The amount of reserves is designed to correspond with the
              number of tokens in circulation. Because these stablecoins are
              backed by traditional assets, they are often the type discussed
              in business payment scenarios where predictable value is a
              priority.
            </P>
            <H3>Crypto-Backed Stablecoins</H3>
            <P>
              Crypto-backed stablecoins use other digital assets as collateral
              instead of traditional currencies. To account for price changes
              in the underlying assets, these stablecoins are usually
              overcollateralized — meaning the value of the collateral exceeds
              the value of the issued tokens. For businesses, this model
              introduces additional considerations. Finance teams need to
              understand how the collateral mechanism works and how changes in
              the underlying assets may affect stability.
            </P>
            <H3>Algorithmic Stablecoins</H3>
            <P>
              Algorithmic stablecoins rely on software-based mechanisms instead
              of direct asset reserves. Their stability depends on rules that
              manage supply and demand within the system. This model carries a
              different set of risks because the stability mechanism depends on
              market behavior and the performance of the underlying protocol.
            </P>
            <P>
              The risks of algorithmic stablecoins became widely known in 2022
              when TerraUSD (UST), one of the world's largest algorithmic
              stablecoins, lost its peg* to the U.S. dollar and collapsed
              within days. The event highlighted that not all stablecoins
              maintain price stability in the same way and reinforced the
              importance of understanding the mechanism behind each model.
            </P>
            <p className="mt-5 border-t border-[hsl(var(--primary))]/10 pt-4 text-sm leading-relaxed text-muted-foreground/80" data-testid="article-footnote-peg">
              * Peg — The reference value a stablecoin is designed to maintain
              — for example, 1 USDC ≈ 1 USD.
            </p>
            <Note testId="article-note-type-choice">
              The type of stablecoin a business chooses can affect transaction
              risk, accounting requirements, and internal controls.
              Understanding the underlying model helps finance teams evaluate
              whether a specific stablecoin fits their operational needs.
            </Note>
          </Section>

          {/* 05 — Stablecoins Among Other Forms of Money */}
          <Section id="stablecoins-among-other-forms-of-money" index={5} title="Stablecoins Among Other Forms of Money">
            <P>
              Stablecoins occupy a space between traditional money and digital
              assets. They share some characteristics with both, while serving
              a specific purpose: enabling digital transfers with a more
              predictable value.
            </P>
            <div className="mt-9 space-y-9" data-testid="article-forms-of-money-blocks">
              <div className="border-t border-[hsl(var(--accent))]/70 pt-6">
                <h3 className="text-xl font-semibold tracking-[-0.01em] text-foreground">
                  Stablecoins and Cryptocurrencies
                </h3>
                <P className="!mt-3.5">
                  Cryptocurrencies such as Bitcoin and Ethereum were created as
                  digital assets for storing and transferring value. Their
                  prices can change significantly, which makes them less
                  predictable for everyday business transactions. Stablecoins
                  are designed around a different goal: maintaining a stable
                  value that can support payment and settlement use cases.
                </P>
              </div>
              <div className="border-t border-[hsl(var(--accent))]/70 pt-6">
                <h3 className="text-xl font-semibold tracking-[-0.01em] text-foreground">
                  Stablecoins and Bank Money
                </h3>
                <P className="!mt-3.5">
                  Bank money remains the primary way most businesses manage
                  payments and financial operations. Traditional banking
                  systems provide established processes, regulatory frameworks,
                  and broad acceptance. At the same time, international
                  payments can involve multiple banks, longer processing times,
                  and additional costs. According to World Bank estimates, the
                  average global cost of sending an international remittance
                  remains around 5–6% of the amount transferred, although costs
                  vary by country and payment corridor. Stablecoins can serve
                  as an additional option for specific payment scenarios where
                  businesses need faster digital settlement across borders.
                </P>
              </div>
              <div className="border-t border-[hsl(var(--accent))]/70 pt-6">
                <h3 className="text-xl font-semibold tracking-[-0.01em] text-foreground">
                  Stablecoins and CBDCs
                </h3>
                <P className="!mt-3.5">
                  Central bank digital currencies (CBDCs) are digital versions
                  of national currencies issued by central banks. The key
                  difference between CBDCs and stablecoins is the issuer and
                  the underlying framework. CBDCs are created and managed by
                  governments as part of the official monetary system.
                  Stablecoins are issued by private organizations and maintain
                  their value through reserve mechanisms or other models.
                </P>
                <P>
                  For businesses, the question is usually less about choosing
                  one universal payment method and more about selecting the
                  right tool for a specific scenario. Bank money remains the
                  foundation of traditional financial operations. Stablecoins
                  may support digital payment workflows where speed and global
                  accessibility are important. CBDCs may become part of future
                  government-backed financial infrastructure.
                </P>
              </div>
            </div>
          </Section>

          {/* 06 — Where Businesses Use Stablecoins Today */}
          <Section id="where-businesses-use-stablecoins-today" index={6} title="Where Businesses Use Stablecoins Today">
            <P>
              Businesses use stablecoins in situations where traditional
              payment methods may create friction, especially for international
              transactions and high-volume payment workflows.
            </P>
            {/* Desktop/tablet — table */}
            <div className="mt-6 hidden overflow-hidden rounded-xl ring-1 ring-[hsl(var(--primary))]/12 sm:block" data-testid="article-use-cases-table">
              <Table>
                <TableHeader>
                  <TableRow className="border-[hsl(var(--primary))]/12 bg-[hsl(var(--primary))]/[0.05] hover:bg-[hsl(var(--primary))]/[0.05]">
                    <TableHead className="h-12 px-5 text-xs font-semibold uppercase tracking-[0.12em] text-foreground">
                      Use Case
                    </TableHead>
                    <TableHead className="h-12 border-l border-[hsl(var(--primary))]/10 px-5 text-xs font-semibold uppercase tracking-[0.12em] text-foreground">
                      How Businesses Use Stablecoins
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {USE_CASES.map(([useCase, description]) => (
                    <TableRow key={useCase} className="border-[hsl(var(--primary))]/10 hover:bg-[hsl(var(--primary))]/[0.02]">
                      <TableCell className="w-[220px] whitespace-normal px-5 py-4 align-top text-sm font-medium leading-relaxed text-foreground">
                        {useCase}
                      </TableCell>
                      <TableCell className="whitespace-normal border-l border-[hsl(var(--primary))]/10 px-5 py-4 align-top text-sm leading-relaxed text-muted-foreground">
                        {description}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            {/* Mobile — stacked blocks */}
            <div className="mt-8 space-y-8 sm:hidden" data-testid="article-use-cases-mobile">
              {USE_CASES.map(([useCase, description]) => (
                <div key={useCase} className="border-t-[3px] border-[hsl(var(--accent))]/70 pt-5">
                  <h3 className="text-lg font-semibold tracking-[-0.01em] text-foreground">{useCase}</h3>
                  <p className="mt-3 text-base leading-[1.75] text-muted-foreground">{description}</p>
                </div>
              ))}
            </div>
            <Note testId="article-note-workflow-questions">
              In every scenario, transferring funds is only one part of the
              workflow. Finance teams still need answers to key questions: Who
              received the payment? What was the business purpose? Which
              documents support the transaction? How should it be recorded?
            </Note>
          </Section>

          {/* 07 — How a Transaction Becomes a Financial Record */}
          <Section id="how-a-transaction-becomes-a-financial-record" index={7} title="How a Transaction Becomes a Financial Record">
            <P>
              Regardless of how a company uses stablecoins, one question
              eventually comes up: how does a digital payment become part of
              the financial record?
            </P>
            <P>
              A stablecoin transaction creates a record on the blockchain that
              includes details such as the sender and recipient addresses,
              transaction amount, timestamp, network, and transaction ID. For
              businesses, this information is only the starting point.
            </P>
            <P>
              For example, a blockchain transaction may show that 5,000 USDC
              was transferred from one wallet to another at 14:37 UTC. It does
              not explain:
            </P>
            <ul className="mt-4 space-y-2.5 pl-1">
              {[
                "which invoice the payment relates to;",
                "whether it was approved internally;",
                "which exchange rate should be used for accounting;",
                "how network fees should be recorded.",
              ].map((li) => (
                <li key={li} className="flex gap-3 text-base leading-relaxed text-muted-foreground">
                  <span className="mt-[0.72em] h-[3px] w-3 shrink-0 bg-[hsl(var(--accent))]" aria-hidden="true" />
                  {li}
                </li>
              ))}
            </ul>
            <P>
              This additional information comes from the company's financial
              processes — not from the blockchain itself.
            </P>
            <Note testId="article-note-blockchain-context">
              A blockchain record shows that a transaction occurred, but
              finance teams need additional context to understand the business
              activity behind it. This includes information about the purpose
              of the payment, the parties involved, and the related
              documentation.
            </Note>
            <P>
              This connection between digital transactions and business records
              is what allows companies to manage reporting, reconciliation, and
              compliance processes effectively. The workflow can be summarized
              as:
            </P>
            {/* Workflow strip */}
            <div
              className="mt-6 flex flex-wrap items-center gap-x-3 gap-y-3 rounded-xl bg-[hsl(var(--primary))]/[0.04] px-6 py-5 ring-1 ring-[hsl(var(--primary))]/10"
              data-testid="article-workflow-strip"
            >
              {[
                "Business activity",
                "Payment",
                "Blockchain record",
                "Business documentation",
                "Financial reporting",
              ].map((step, i, arr) => (
                <span key={step} className="flex items-center gap-3">
                  <span className="text-sm font-medium text-foreground">{step}</span>
                  {i < arr.length - 1 && (
                    <ArrowRight className="h-3.5 w-3.5 text-[hsl(var(--accent))]" strokeWidth={2} aria-hidden="true" />
                  )}
                </span>
              ))}
            </div>
            <P>
              Each step answers a different question: What transaction took
              place? Who was involved? Why was the payment made? How should it
              be reflected in financial records? As digital payment methods
              develop, finance teams will play an important role in connecting
              new payment technologies with existing accounting and reporting
              processes.
            </P>
          </Section>

          {/* 08 — Accounting and tax around the world */}
          <Section id="how-stablecoins-are-accounted-for-and-taxed" index={8} title="How Stablecoins Are Accounted for and Taxed Around the World">
            <P>
              Using stablecoins creates accounting and tax considerations that
              differ from traditional payment methods. Although stablecoins are
              designed to maintain a stable value, many jurisdictions treat
              them as digital assets rather than legal currency. The exact
              treatment depends on local regulations, the type of stablecoin,
              and how it is used in business operations.
            </P>
            <H3>United States</H3>
            <P>
              The U.S. Internal Revenue Service (IRS) generally treats
              stablecoins as digital assets rather than currency. This approach
              follows the IRS guidance on virtual currency taxation, including
              Notice 2014-21 and subsequent clarifications. Transactions
              involving stablecoins may create taxable events when they are
              sold, exchanged for another asset, or used to pay for goods and
              services.
            </P>
            <P>
              Because most fiat-backed stablecoins are designed to maintain a
              1:1 value with the U.S. dollar, gains or losses on individual
              transactions are often minimal. However, businesses still need to
              track transaction details and report applicable activity. For
              example, a company that receives payment in USDC may need to
              record:
            </P>
            <ul className="mt-4 space-y-2.5 pl-1">
              {[
                "the fair market value at the time of receipt;",
                "the date of the transaction;",
                "the business purpose of the payment;",
                "any later conversion or disposal of the asset.",
              ].map((li) => (
                <li key={li} className="flex gap-3 text-base leading-relaxed text-muted-foreground">
                  <span className="mt-[0.72em] h-[3px] w-3 shrink-0 bg-[hsl(var(--accent))]" aria-hidden="true" />
                  {li}
                </li>
              ))}
            </ul>
            <P>
              Starting in 2026, certain digital asset brokers and platforms
              will also begin reporting transaction proceeds to the IRS through
              Form 1099-DA, increasing the importance of accurate digital asset
              records.
            </P>
            <H3>European Union</H3>
            <P>
              In the European Union, stablecoins are regulated under the
              Markets in Crypto-Assets Regulation (MiCA), which introduced
              specific categories for crypto-assets linked to official
              currencies. Stablecoins linked to a single official currency,
              such as the euro, may fall under the category of Electronic Money
              Tokens (EMTs). These assets are designed to maintain a stable
              value by referencing a fiat currency and are subject to specific
              requirements around issuance, reserves, and consumer protection.
            </P>
            <P>
              However, tax treatment remains largely determined by individual
              EU member states. For example, in Italy, certain euro-denominated
              EMTs that comply with the MiCA framework may receive different
              tax treatment compared with other crypto-assets. Under current
              rules, some regulated EMTs may be subject to a 26% tax rate
              instead of the higher rate applied to certain other
              crypto-assets, and redemption back into euros at face value may
              not be treated as a capital gain event.
            </P>
            <P>
              This illustrates an important point for businesses operating in
              Europe: the same stablecoin may have different accounting and tax
              implications depending on the country where the company is based
              and how the asset is classified.
            </P>
            <H3>United Kingdom</H3>
            <P>
              In the United Kingdom, HMRC treats stablecoins as exchange tokens
              rather than traditional currency, applying the same tax
              principles used for other crypto-assets. When a business disposes
              of stablecoins — by exchanging them for another asset, converting
              them into fiat currency, or using them to pay for goods or
              services — the transaction creates a capital gains tax event. The
              taxable gain is calculated as the difference between the asset's
              value at acquisition and its value at disposal.
            </P>
            <P>
              Stablecoins received as payment for goods or services are treated
              as income, based on their market value at the time of receipt.
              The UK has not introduced a separate tax regime for regulated
              stablecoins, unlike some other jurisdictions. Businesses
              therefore apply the same crypto-asset reporting and
              record-keeping principles used for other digital assets.
            </P>
            <H3>Canada</H3>
            <P>
              The Canada Revenue Agency (CRA) generally treats stablecoins as
              property rather than legal tender. This means that transactions
              involving stablecoins may require businesses to determine the
              value of the asset at the time of acquisition, exchange, or use.
              The tax treatment depends on the nature of the activity:
              investment activity may be treated as a capital transaction,
              where 50% of the capital gain is generally taxable; business
              activities may be treated as business income, where the full
              amount may be included in taxable income.
            </P>
            <P>
              Canada is also developing a more detailed regulatory framework
              for payment stablecoins, including requirements related to
              reserve management, registration, and financial oversight.
            </P>
            <H3>Common Considerations for Finance Teams</H3>
            <P>
              Although regulations differ across jurisdictions, companies using
              stablecoins generally need to focus on several recurring areas:
            </P>
            <ul className="mt-4 space-y-3 pl-1">
              {[
                ["Transaction records", "Blockchain data provides information about the movement of assets, but businesses usually need additional records connecting transactions to their commercial purpose."],
                ["Valuation", "Companies may need to determine the value of stablecoins at specific points in time for accounting and tax purposes."],
                ["Documentation", "Payment details, counterparties, invoices, approvals, and supporting documents help create a complete financial record."],
                ["Local requirements", "Businesses operating across multiple countries need to consider the tax and reporting rules applicable in each jurisdiction."],
              ].map(([lead, rest]) => (
                <li key={lead} className="flex gap-3 text-base leading-relaxed text-muted-foreground">
                  <span className="mt-[0.72em] h-[3px] w-3 shrink-0 bg-[hsl(var(--accent))]" aria-hidden="true" />
                  <span>
                    <span className="font-medium text-foreground">{lead}:</span> {rest}
                  </span>
                </li>
              ))}
            </ul>
            <Note testId="article-note-compliance-challenge">
              For companies using stablecoins in payments, the biggest
              challenge is often not the transaction itself — it is creating
              the processes needed to support accurate reporting and
              compliance.
            </Note>
          </Section>

          {/* 09 — Benefits and Considerations */}
          <Section id="benefits-and-considerations" index={9} title="Benefits and Considerations of Stablecoins">
            <P>
              Stablecoins can offer businesses new options for managing
              payments and digital transactions. At the same time, companies
              need clear processes to manage accounting, compliance, and
              operational requirements. The value of stablecoins depends on the
              business use case, existing payment infrastructure, and
              regulatory environment.
            </P>
            <div className="mt-6 overflow-hidden rounded-xl ring-1 ring-[hsl(var(--primary))]/12" data-testid="article-benefits-table">
              <Table>
                <TableHeader>
                  <TableRow className="border-[hsl(var(--primary))]/12 bg-[hsl(var(--primary))]/[0.05] hover:bg-[hsl(var(--primary))]/[0.05]">
                    <TableHead className="h-12 px-5 text-xs font-semibold uppercase tracking-[0.12em] text-foreground">
                      Potential Benefits
                    </TableHead>
                    <TableHead className="h-12 border-l border-[hsl(var(--primary))]/10 px-5 text-xs font-semibold uppercase tracking-[0.12em] text-foreground">
                      Key Considerations
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {[
                    ["Faster cross-border payments and settlements", "Companies need to understand applicable regulations and internal policies"],
                    ["Access to digital payment networks", "Transactions require proper controls, documentation, and record keeping"],
                    ["Support for global payout workflows", "Finance teams need clear processes for accounting and reporting"],
                    ["Transparent transaction history through blockchain records", "Blockchain data alone may not provide all information required for financial reporting"],
                    ["Integration with modern financial infrastructure", "Businesses should evaluate operational readiness before adoption"],
                  ].map(([benefit, consideration]) => (
                    <TableRow key={benefit} className="border-[hsl(var(--primary))]/10 hover:bg-[hsl(var(--primary))]/[0.02]">
                      <TableCell className="whitespace-normal px-5 py-4 align-top text-sm leading-relaxed text-muted-foreground">
                        {benefit}
                      </TableCell>
                      <TableCell className="whitespace-normal border-l border-[hsl(var(--primary))]/10 px-5 py-4 align-top text-sm leading-relaxed text-muted-foreground">
                        {consideration}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <P>
              The right approach depends on the company's goals, industry, and
              operating environment. Many organizations begin by evaluating
              specific payment scenarios before expanding their use of
              stablecoins.
            </P>
          </Section>

          {/* 10 — The Future of Stablecoins in Business Finance */}
          <Section id="future-of-stablecoins" index={10} title="The Future of Stablecoins in Business Finance">
            <P>
              Stablecoins and central bank digital currencies (CBDCs) continue
              to develop alongside traditional financial systems. While the
              future role of each technology is still taking shape, digital
              assets are becoming an important topic for finance teams.
            </P>
            <P>
              Companies do not need to adopt every new payment technology
              immediately. But understanding how these tools work helps finance
              professionals make informed decisions when new opportunities or
              requirements emerge. For finance teams, the key questions remain
              the same: What business problem does this technology solve? How
              does it fit into existing financial processes? What data,
              controls, and documentation are required? How should transactions
              be tracked and reported?
            </P>
            <P>
              Stablecoins represent one part of the broader shift toward
              digital financial infrastructure. By building knowledge of these
              tools today, finance teams can better evaluate how they may fit
              into future payment and operational workflows.
            </P>
          </Section>
        </article>
      </div>
    </Container>
  </section>
);

/* ---------------- Final CTA ---------------- */

const ArticleFinalCta = () => (
  <section data-testid="article-final-cta-section">
    <Container className="pb-[60px] lg:pb-[120px]">
      <div className="wfii-stipple-dark relative overflow-hidden rounded-3xl bg-primary p-10 text-center text-primary-foreground shadow-[0_40px_80px_-32px_hsl(218_51%_15%/0.55)] sm:p-16">
        <div
          className="wfii-wash-teal-dark pointer-events-none absolute -left-40 -bottom-52 h-[480px] w-[480px]"
          aria-hidden="true"
        />
        <div
          className="wfii-wash-teal-dark pointer-events-none absolute -right-48 -top-48 h-[420px] w-[420px]"
          aria-hidden="true"
        />
        <div className="relative">
          <h2 className="font-serif-editorial mx-auto max-w-3xl text-3xl font-semibold leading-[1.12] tracking-[-0.02em] sm:text-4xl lg:max-w-none lg:whitespace-nowrap lg:text-5xl">
            Turning Knowledge Into a Working Process
          </h2>
          <div className="mx-auto max-w-2xl lg:max-w-[880px]">
            <p className="mt-7 text-base leading-relaxed text-primary-foreground/75 sm:text-lg">
              This guide covered how stablecoins work and what finance teams
              should consider before{" "}
              <br className="hidden lg:block" />
              using them. WFII programs pick up from there, helping finance
              teams build the accounting{" "}
              <br className="hidden lg:block" />
              workflows, controls, and reporting processes that turn that
              understanding into practice.
            </p>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
              <Button
                asChild
                className="h-12 bg-primary-foreground px-7 text-sm font-medium text-primary shadow-[0_16px_32px_-12px_hsl(0_0%_0%/0.35)] transition-colors hover:bg-[hsl(var(--accent))] hover:text-white active:scale-[0.98]"
                data-testid="article-final-cta-explore-programs-button"
              >
                <Link to="/programs">Explore Programs</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Container>
  </section>
);

/* ---------------- Page ---------------- */

export default function ArticleStablecoinsGuidePage() {
  return (
    <div className="relative min-h-screen">
      <div className="wfii-canvas wfii-noise" aria-hidden="true" data-testid="page-canvas" />
      <div className="relative z-10 flex min-h-screen flex-col">
        <Header />
        <ReadingProgressBar />
        <main className="wfii-longform flex-1 space-y-[60px] lg:space-y-[100px]">
          <ArticleHero />
          <ArticleBody />
          <ArticleFinalCta />
        </main>
        <Footer />
      </div>
    </div>
  );
}
